import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { Stack, ControlLink, Overlay, Text, LoadingBalls } from '@moda/om';
import { formatPrice } from '@moda/portal-stanchions';
import { reportError } from '../../lib/reportError';
import { useCart } from '../../hooks/useCart';
import { useUser } from '../../hooks/useUser';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useReward } from './useReward';

import './RewardSummary.scss';

enum Mode {
  Resting,
  Saving,
  Applied
}

interface Props {
  theme?: 'box' | 'simple';
  format: 'applied' | 'negative';
}

export const RewardSummary: React.FC<Props> = ({ theme = 'box', format }) => {
  const { cart } = useCart();
  const { user } = useUser();
  const { showErrorModal } = useErrorModal();

  const rewardSummary = cart.attributes.rewardSummary;
  const rewardsAmount = cart.attributes.paymentSummary.rewardsAmount;

  const [mode, setMode] = useState(user.userLoyaltyStatus ? Mode.Applied : Mode.Resting);
  const { applyReward } = useReward();

  const submitCode = useCallback(
    async (reward: boolean) => {
      setMode(Mode.Saving);

      try {
        const result = await applyReward(reward);
        if (result?.data?.updateCart.data[0].attributes.rewardSummary?.useLoyaltyRewards) {
          setMode(Mode.Applied);
        } else {
          setMode(Mode.Resting);
        }
      } catch (error) {
        setMode(Mode.Resting);
        showErrorModal();
        reportError('REWARD_SUMMARY_SUBMIT_CODE', error);
      }
    },
    [setMode, applyReward, showErrorModal]
  );

  const onApply = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      submitCode(true);
    },
    [submitCode]
  );

  const onRemove = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault();
      submitCode(false);
    },
    [submitCode]
  );

  if (!rewardSummary) return null;

  if (!rewardSummary.enrolledForLoyalty) return null;

  if (!rewardSummary.redeemableAmount.price) return null;

  if (rewardSummary?.redeemableAmount.price <= 0) return null;

  return (
    <>
      <Stack space={0} className={classNames('RewardSummary', `RewardSummary--${theme}`)}>
        <div className="RewardSummary__head">
          <div className="RewardSummary__title">M'O Rewards</div>

          {!rewardSummary?.useLoyaltyRewards && (
            <ControlLink
              className="RewardSummary__apply"
              aria-label="Add Rewards"
              onClick={onApply}
            >
              + Apply
            </ControlLink>
          )}

          {rewardSummary.useLoyaltyRewards && (
            <>
              {format === 'negative' && (
                <div className="RewardSummary__content-right">
                  {`-${formatPrice(rewardsAmount.currency, rewardsAmount.price)}`}
                </div>
              )}

              {format === 'applied' && (
                <div className="RewardSummary__content">
                  {`${formatPrice(rewardsAmount.currency, rewardsAmount.price)} rewards applied`}
                </div>
              )}

              <Stack space={0} direction="horizontal" justifyContent="flex-end">
                <ControlLink
                  className="RewardSummary__remove"
                  aria-label="Remove Rewards"
                  onClick={onRemove}
                >
                  Remove Rewards
                </ControlLink>
              </Stack>
            </>
          )}
        </div>

        {!rewardSummary.useLoyaltyRewards && (
          <div className="RewardSummary__content">
            {`${formatPrice(
              rewardSummary.redeemableAmount.currency,
              rewardSummary.redeemableAmount.price
            )} in rewards available.`}
          </div>
        )}
      </Stack>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
